// Meta Pixel (browser-side) for the lifetime launch ads.
//
// Pixel id comes from VITE_META_PIXEL_ID. Unset in local/staging builds —
// every function here becomes a no-op rather than throwing, so dev and
// staging stay clean of test traffic.
//
// Standard Meta base code, adapted to TypeScript and gated on the env var
// instead of being hard-coded into index.html (keeps the id out of version
// control history and lets staging/production diverge without a second
// index.html).
//
// Dedup with server-side CAPI (apps/api/src/stripe/webhook.ts +
// apps/api/src/meta/capi.ts): an `eventID` passed to `trackEvent` here must
// exactly match the `event_id` the server sends for the same logical event —
// for Purchase, that's the Stripe checkout session id (`cs_...`).

declare global {
  interface Window {
    fbq?: FbqFunction & { queue?: unknown[]; loaded?: boolean; version?: string };
    _fbq?: unknown;
  }
}

type FbqFunction = (...args: unknown[]) => void;

const PIXEL_ID = (import.meta.env.VITE_META_PIXEL_ID as string | undefined)?.trim();

let initialised = false;

/** True once VITE_META_PIXEL_ID is set and initMetaPixel() has run. */
export function isMetaPixelEnabled(): boolean {
  return Boolean(PIXEL_ID) && initialised;
}

/**
 * Loads the Meta Pixel base code once and fires the first PageView.
 * No-op (and logs nothing) when VITE_META_PIXEL_ID is unset — local and
 * staging builds must never send test traffic into the production dataset.
 */
export function initMetaPixel(): void {
  if (!PIXEL_ID || initialised || typeof window === "undefined") return;
  initialised = true;

  // Meta's standard base code, verbatim in structure (minified fbq shim +
  // async script tag), just written as TS instead of inline HTML.
  const w = window;
  if (!w.fbq) {
    type Fbq = FbqFunction & {
      callMethod?: FbqFunction;
      queue?: unknown[];
      loaded?: boolean;
      version?: string;
    };
    const fbq = function (...args: unknown[]) {
      if (fbq.callMethod) {
        fbq.callMethod(...args);
      } else {
        (fbq.queue ??= []).push(args);
      }
    } as Fbq;
    fbq.queue = [];
    fbq.loaded = true;
    fbq.version = "2.0";
    w.fbq = fbq;
    if (!w._fbq) w._fbq = fbq;

    // The shim above queues every call, so nothing is lost by fetching the
    // real script a moment later. Waiting for the browser to go idle keeps
    // about 270 KB of third-party script from competing with first paint;
    // the timeout bounds the wait on a busy page.
    const load = () => {
      const script = document.createElement("script");
      script.async = true;
      script.src = "https://connect.facebook.net/en_US/fbevents.js";
      document.head.appendChild(script);
    };
    if ("requestIdleCallback" in w) {
      w.requestIdleCallback(load, { timeout: 2500 });
    } else {
      setTimeout(load, 1);
    }
  }

  w.fbq("init", PIXEL_ID);
  w.fbq("track", "PageView");
}

/** Fire on every SPA route change after the first load (see main.tsx). */
export function trackPageView(): void {
  if (!isMetaPixelEnabled()) return;
  window.fbq?.("track", "PageView");
}

/**
 * Fire a standard or custom Meta Pixel event.
 *
 * `eventID` is Meta's browser/server dedup key — pass the same value to the
 * matching CAPI call server-side. Omit it for events with no server-side
 * counterpart (ViewContent, InitiateCheckout, PageView).
 */
export function trackEvent(
  name: string,
  params?: Record<string, unknown>,
  eventID?: string,
): void {
  if (!isMetaPixelEnabled()) return;
  if (eventID) {
    window.fbq?.("track", name, params ?? {}, { eventID });
  } else {
    window.fbq?.("track", name, params ?? {});
  }
}

/** Reads a cookie value by name, or "" if absent. Shared by lifetime pages. */
export function readPixelCookie(name: string): string {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : "";
}

/**
 * sessionStorage-guarded "fire once per key" check, used so a reload of
 * /lifetime/thank-you (or a repeat poll) never double-fires Purchase in the
 * browser. Wrapped in try/catch: private browsing / blocked storage must
 * degrade to "always fire" rather than throw.
 */
export function markFiredOnce(key: string): boolean {
  try {
    if (window.sessionStorage.getItem(key) === "1") return false;
    window.sessionStorage.setItem(key, "1");
    return true;
  } catch {
    return true;
  }
}
